/**
 * "On the menu" teaser — a strip of the day's real item photos (bundle images
 * first, per the week-preview endpoint) with a "+N" overflow tile and a short
 * name caption. Extracted verbatim from the family week card (PROD-12) so the
 * school admin week view can show the exact same presentation below its order
 * counts. Read-only; the parent card owns the click affordance.
 */

import { Gift, UtensilsCrossed } from 'lucide-react'
import { type ReactElement } from 'react'

import type { MenuWeekPreviewDay } from '@/api/orders'
import { AssetImage } from '@/components/asset-image'

export function OnTheMenuStrip({ preview }: { preview: MenuWeekPreviewDay }): ReactElement {
  const shown = preview.items.slice(0, 4)
  const extra = preview.totalCount - shown.length
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">On the menu</span>
      <div className="flex flex-wrap items-center gap-2">
        {shown.map((item) => (
          <div
            key={item.menuItemId}
            className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-muted"
            title={item.name}>
            <AssetImage
              src={item.imageUri}
              alt={item.name}
              className="h-full w-full object-cover"
              fallback={
                <span className="grid h-full w-full place-items-center">
                  <UtensilsCrossed className="h-5 w-5 text-muted-foreground" />
                </span>
              }
            />
          </div>
        ))}
        {extra > 0 ? (
          <span className="grid h-14 min-w-14 shrink-0 place-items-center rounded-lg bg-muted px-2 text-sm font-medium text-muted-foreground">
            +{extra}
          </span>
        ) : null}
      </div>
      <span className="text-xs text-muted-foreground">
        {shown
          .slice(0, 2)
          .map((item) => item.name)
          .join(' · ')}
        {preview.totalCount > 2 ? ` · +${preview.totalCount - 2} more` : ''}
      </span>
      {preview.holidayTreat ? (
        <span className="flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
          <Gift className="h-3.5 w-3.5" strokeWidth={1.75} />
          Free {preview.holidayTreat.name.toLowerCase()} with every{' '}
          {preview.holidayTreat.mealtime === 'SUPPER' ? 'supper' : 'lunch'}
        </span>
      ) : null}
    </div>
  )
}

/**
 * The teaser worth rendering for one day of a week-preview response: the
 * matching day when it has items and the school is serving. Pure so the
 * gating is unit-testable apart from the query plumbing.
 */
export function dayMenuTeaser(
  days: readonly MenuWeekPreviewDay[] | undefined,
  dateKey: string,
): MenuWeekPreviewDay | null {
  const day = days?.find((d) => d.date === dateKey)
  if (!day || day.noService || day.items.length === 0) {
    return null
  }
  return day
}
